import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { Doc } from 'convex/_generated/dataModel';
import type { IntelligenceKey } from 'schemas/intelligenceSchema';
import { useForm } from 'react-hook-form';
import SoftSkillConfig from '~/components/skills/SoftSkillConfig';
import { BasicSkillFields } from '~/components/skills/shared/BasicSkillFields';
import { SkillFormActions } from '~/components/skills/shared/SkillFormActions';
import { Form } from '@reactor/ui/form';
import { useSkillFormSubmit } from '~/hooks/useSkillFormSubmit';
import {
	buildSoftSkillFromForm,
	getDefaultSoftSkill,
	softSkillFormSchema,
	SoftSkillFormValues,
	SoftSkillFormInput,
} from '~/lib/skill-form-utils';

interface SoftSkillFormProps {
	skill?: Doc<'skills'>;
	isEditable?: boolean;
}

export function SoftSkillForm({ skill, isEditable = true }: SoftSkillFormProps) {
	//
	const form = useForm<SoftSkillFormInput, unknown, SoftSkillFormValues>({
		resolver: standardSchemaResolver(softSkillFormSchema),
		defaultValues: getDefaultSoftSkill(skill),
		mode: 'onChange',
	});

	const { submitSkill, handleFormError, isSubmitting } = useSkillFormSubmit(skill, buildSoftSkillFromForm);

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(submitSkill, handleFormError)} className="space-y-6">
				{/* Basic skill fields */}
				<BasicSkillFields form={form} isEditable={isEditable} skill={skill} />

				{/* Soft skill specific configuration */}
				<SoftSkillConfig
					model={form.watch('config.model') as IntelligenceKey | 'auto'}
					onModelChange={(value: IntelligenceKey | 'auto') => form.setValue('config.model', value as any)}
					temperature={form.watch('config.temperature')}
					onTemperatureChange={(value: number) => form.setValue('config.temperature', value)}
					instructions={form.watch('config.instructions')}
					onInstructionsChange={(value: string) => form.setValue('config.instructions', value)}
					availableSkills={form.watch('config.availableSkills')}
					onAvailableSkillsChange={(skills: string[]) => form.setValue('config.availableSkills', skills)}
					isEditable={isEditable}
				/>

				{/* Form Actions */}
				<SkillFormActions isSubmitting={isSubmitting} isEditable={isEditable} />
			</form>
		</Form>
	);
}
